import { useState } from 'react';
import { COMPANY_ROLES, COMPANY_ROLE_LABEL, type CompanyRole } from 'shared';
import { api, ApiError } from '../api';
import FieldError from '../FieldError';
import { useFormErrors } from '../useFormErrors';
import { copyText } from '../telegram';
import { toast } from '../toast';

interface Props {
  companyId: string;
  onDone: () => void;
}

interface Created {
  login: string;
  password: string;
  name: string;
}

/**
 * Найм сотрудника напрямую.
 *
 * Код приглашения годится, когда у человека уже есть аккаунт и Telegram.
 * У наёмного водителя часто нет ни того, ни другого — а работать он должен
 * завтра. Поэтому учётную запись заводит контора и отдаёт логин с паролем
 * в руки, вместе с ключами от машины.
 *
 * Пароль показывается ровно один раз: в базе лежит только хеш. Потеряют —
 * владелец сбросит, но восстановить прежний нельзя.
 */
export default function CrewInvite({ companyId, onDone }: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<Created | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [login, setLogin] = useState('');
  const [tgUsername, setTgUsername] = useState('');
  const [role, setRole] = useState<CompanyRole>('DRIVER');
  const [canDrive, setCanDrive] = useState(false);
  const [canSeeRates, setCanSeeRates] = useState(false);
  const [canBook, setCanBook] = useState(false);

  const form = useFormErrors();

  async function submit() {
    // Проверка повторяет серверную: до сети видно, чего не хватает,
    // и человек не ждёт ответа ради «укажите телефон».
    if (firstName.trim().length < 2) return form.fail({ firstName: 'Укажите имя' });
    if (!/^\+?[0-9]{10,15}$/.test(phone.trim())) {
      return form.fail({ phone: 'Телефон в формате +79001234567' });
    }
    if (!/^[a-zA-Z0-9._-]{3,32}$/.test(login.trim())) {
      return form.fail({ login: 'Латиница и цифры, от 3 символов' });
    }

    form.reset();
    setBusy(true);
    try {
      const res = await api.createMember(companyId, {
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        phone: phone.trim(),
        email: email.trim() || undefined,
        login: login.trim(),
        tgUsername: tgUsername.trim().replace(/^@/, '') || undefined,
        roles: [role],
        // Водителю руль подразумевается, диспетчеру — только по галочке.
        canDrive: role === 'DRIVER' || canDrive,
        canSeeRates,
        canBook,
      });

      setCreated({
        login: res.login,
        password: res.password,
        name: [firstName, lastName].filter(Boolean).join(' '),
      });
      toast.success(`${res.login} заведён — передайте пароль лично`);

      setFirstName('');
      setLastName('');
      setPhone('');
      setEmail('');
      setLogin('');
      setTgUsername('');
      onDone();
    } catch (e) {
      const issues = e instanceof ApiError ? e.issues : undefined;
      if (issues?.length) {
        form.fail(Object.fromEntries(issues.map((i) => [i.field, i.message])));
      } else {
        toast.error((e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  }

  if (created) {
    return (
      <div className="card">
        <h3>{created.name} заведён</h3>
        <div className="meta" style={{ marginBottom: 'var(--s3)' }}>
          Передайте эти данные лично. Пароль показывается один раз — в базе
          хранится только его отпечаток.
        </div>

        {/* Копирование подтверждается вслух: без ответа человек нажимает
            второй раз и вставляет неизвестно что. */}
        <div
          className="invite"
          role="button"
          tabIndex={0}
          onClick={() => {
            void copyText(created.login);
            toast.success('Логин скопирован');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              void copyText(created.login);
              toast.success('Логин скопирован');
            }
          }}
        >
          <span>{created.login}</span>
          <span className="meta">логин · нажмите, чтобы скопировать</span>
        </div>
        <div
          className="invite"
          role="button"
          tabIndex={0}
          onClick={() => {
            void copyText(created.password);
            toast.success('Пароль скопирован — вставьте его водителю');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              void copyText(created.password);
              toast.success('Пароль скопирован — вставьте его водителю');
            }
          }}
        >
          <span>{created.password}</span>
          <span className="meta">пароль · нажмите, чтобы скопировать</span>
        </div>

        <button
          className="secondary"
          onClick={() => {
            setCreated(null);
            setOpen(false);
          }}
        >
          Готово
        </button>
      </div>
    );
  }

  if (!open) {
    // Не карточка во всю ширину: это вспомогательное действие, а не главное
    // на экране. Полоса во всю рабочую область читается как заголовок.
    return (
      <button
        className="secondary btn--compact"
        style={{ marginBottom: 'var(--s3)' }}
        onClick={() => setOpen(true)}
      >
        Завести сотрудника
      </button>
    );
  }

  return (
    <div className="card">
      <h3>Новый сотрудник</h3>

      <div className="field-row">
        <div className="field">
          <label>Имя</label>
          <input
            {...form.field('firstName')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <FieldError form={form} name="firstName" />
        </div>
        <div className="field">
          <label>Фамилия</label>
          <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
      </div>

      <div className="field">
        <label>Телефон</label>
        <input
          {...form.field('phone')}
          inputMode="tel"
          placeholder="+79001234567"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <FieldError form={form} name="phone" />
      </div>

      <div className="field">
        <label>Почта (необязательно)</label>
        <input
          {...form.field('email')}
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <FieldError form={form} name="email" />
      </div>

      <div className="field">
        <label>Telegram</label>
        <input
          {...form.field('tgUsername')}
          autoCapitalize="none"
          placeholder="@ivanov"
          value={tgUsername}
          onChange={(e) => setTgUsername(e.target.value)}
        />
        <FieldError form={form} name="tgUsername" />
        {/*
          * Telegram нужен не для входа, а для слежения за машиной: Mini App
          * в фоне геопозицию отдавать не умеет, и постоянную трансляцию
          * ведёт бот. Без него рейс будет виден только со слов водителя.
          */}
        <div className="meta" style={{ marginTop: 4 }}>
          Нужен, чтобы видеть машину на карте: трансляцию геопозиции ведёт
          бот. Без Telegram водитель сможет только отмечаться вручную.
        </div>
      </div>

      <div className="field">
        <label>Логин для входа</label>
        <input
          {...form.field('login')}
          autoCapitalize="none"
          value={login}
          onChange={(e) => setLogin(e.target.value.toLowerCase())}
        />
        <FieldError form={form} name="login" />
      </div>

      <div className="field">
        <label>Кем работает</label>
        <select value={role} onChange={(e) => setRole(e.target.value as CompanyRole)}>
          {/* Владельца через найм не заводят: смена собственника —
              отдельное осознанное действие. */}
          {COMPANY_ROLES.filter((r) => r !== 'OWNER').map((r) => (
            <option key={r} value={r}>
              {COMPANY_ROLE_LABEL[r]}
            </option>
          ))}
        </select>
      </div>

      {/* Диспетчера за руль по умолчанию не ставят: без галочки его нет
          в подборе экипажа. Водителю она не нужна — он и так водитель. */}
      {role !== 'DRIVER' && (
        <label className="check">
          <input
            type="checkbox"
            checked={canDrive}
            onChange={(e) => setCanDrive(e.target.checked)}
          />
          можно прикреплять к машине
        </label>
      )}

      <label className="check">
        <input
          type="checkbox"
          checked={canSeeRates}
          onChange={(e) => setCanSeeRates(e.target.checked)}
        />
        видит суммы сделок
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={canBook}
          onChange={(e) => setCanBook(e.target.checked)}
        />
        может сам брать грузы
      </label>

      <div className="row" style={{ gap: 8, marginTop: 12 }}>
        <button disabled={busy} onClick={() => void submit()}>
          {busy ? 'Завожу…' : 'Завести'}
        </button>
        <button className="secondary" disabled={busy} onClick={() => setOpen(false)}>
          Отмена
        </button>
      </div>
    </div>
  );
}
